import {
  normalizeWarehouseLocationInput,
  normalizeWarehouseMovementInput,
  normalizeWarehouseStockSearchInput,
  warehouseCountDifference,
  type OpenWarehouseCountInput,
  type PostWarehouseCountInput,
  type PostWarehouseMovementInput,
  type SaveWarehouseCountLineInput,
  type SaveWarehouseLocationInput,
  type WarehouseArticleOption,
  type WarehouseCountLineRecord,
  type WarehouseCountSessionRecord,
  type WarehouseCountStatus,
  type WarehouseGateway,
  type WarehouseLocationRecord,
  type WarehouseMovementRecord,
  type WarehouseOverview,
  type WarehouseReplenishmentRecord,
  type WarehouseStockPage,
  type WarehouseStockRecord,
  type WarehouseStockSearchInput,
} from '@forq/warehouse-data';

interface CountLineState {
  readonly id: string;
  readonly itemId: string;
  readonly locationId: string;
  readonly bookQuantity: number;
  countedQuantity: number | null;
}

interface CountSessionState {
  readonly id: string;
  readonly reference: string;
  status: WarehouseCountStatus;
  readonly locationId: string | null;
  readonly note: string;
  readonly createdAt: string;
  postedAt: string | null;
  readonly lines: CountLineState[];
}

interface BalanceState {
  readonly itemId: string;
  readonly locationId: string;
  onHand: number;
  reserved: number;
  updatedAt: string;
}

interface TenantWarehouseState {
  readonly locations: WarehouseLocationRecord[];
  readonly articles: WarehouseArticleOption[];
  readonly balances: BalanceState[];
  readonly movements: WarehouseMovementRecord[];
  readonly countSessions: CountSessionState[];
}

function clone<T>(value: T): T {
  return structuredClone(value);
}

function tenantPrefix(tenantId: string): '1' | '2' | '3' {
  if (tenantId === 'stw' || tenantId.startsWith('2222')) return '2';
  if (tenantId === 'gsl' || tenantId.startsWith('3333')) return '3';
  return '1';
}

function now(): string {
  return new Date().toISOString();
}

function createState(tenantId: string): TenantWarehouseState {
  const prefix = tenantPrefix(tenantId);
  const createdAt = now();
  const locations: WarehouseLocationRecord[] = [
    {
      id: `${tenantId}-location-main`, tenantId, code: 'H01', name: 'Hauptlager',
      description: 'Zentraler Wareneingang und Ersatzteilbestand', active: true, defaultLocation: true,
      stockedItemCount: 3, createdAt, updatedAt: createdAt,
    },
    {
      id: `${tenantId}-location-workshop`, tenantId, code: 'W01', name: 'Werkstatt',
      description: 'Schnellzugriff für laufende Werkstattaufträge', active: true, defaultLocation: false,
      stockedItemCount: 1, createdAt, updatedAt: createdAt,
    },
    {
      id: `${tenantId}-location-van`, tenantId, code: 'SW1', name: 'Servicewagen 1',
      description: 'Mobiler Monteurbestand', active: true, defaultLocation: false,
      stockedItemCount: 1, createdAt, updatedAt: createdAt,
    },
  ];
  const articles: WarehouseArticleOption[] = [
    {
      id: `${tenantId}-article-filter`, number: `${prefix}0001`, name: 'Hydraulikfilter Toyota', unit: 'piece',
      minimumStock: 5, preferredSupplierId: `${tenantId}-supplier-tvh`, preferredSupplierName: 'TVH Parts NV',
      preferredSupplierItemNumber: 'TVH-4711', preferredPurchasePrice: 24.5, preferredLeadTimeDays: 2,
    },
    {
      id: `${tenantId}-article-wheel`, number: `${prefix}0002`, name: 'Lastrad 230 × 75', unit: 'piece',
      minimumStock: 4, preferredSupplierId: `${tenantId}-supplier-local`, preferredSupplierName: 'Staplerteile Westfalen GmbH',
      preferredSupplierItemNumber: 'LR-23075', preferredPurchasePrice: 78.9, preferredLeadTimeDays: 4,
    },
    {
      id: `${tenantId}-article-oil`, number: `${prefix}0003`, name: 'Hydrauliköl HLP 46', unit: 'liter',
      minimumStock: 10, preferredSupplierId: `${tenantId}-supplier-oil`, preferredSupplierName: 'Schmierstoffhandel Werl',
      preferredSupplierItemNumber: 'HLP46-20', preferredPurchasePrice: 3.25, preferredLeadTimeDays: 1,
    },
    {
      id: `${tenantId}-article-fuse`, number: `${prefix}0004`, name: 'Flachsicherung 80 A', unit: 'piece',
      minimumStock: 6, preferredSupplierId: null, preferredSupplierName: '', preferredSupplierItemNumber: '',
      preferredPurchasePrice: 0, preferredLeadTimeDays: null,
    },
  ];
  const balances: BalanceState[] = [
    { itemId: articles[0].id, locationId: locations[0].id, onHand: 8, reserved: 1, updatedAt: createdAt },
    { itemId: articles[1].id, locationId: locations[0].id, onHand: 1, reserved: 0, updatedAt: createdAt },
    { itemId: articles[2].id, locationId: locations[0].id, onHand: 12, reserved: 2, updatedAt: createdAt },
    { itemId: articles[0].id, locationId: locations[1].id, onHand: 2, reserved: 0, updatedAt: createdAt },
    { itemId: articles[2].id, locationId: locations[2].id, onHand: 3, reserved: 0, updatedAt: createdAt },
  ];
  const movements: WarehouseMovementRecord[] = [
    {
      id: `${tenantId}-movement-1`, tenantId, type: 'receipt', itemId: articles[0].id,
      itemNumber: articles[0].number, itemName: articles[0].name, quantity: 5,
      sourceLocationId: null, sourceLocationCode: '', sourceLocationName: '',
      targetLocationId: locations[0].id, targetLocationCode: locations[0].code, targetLocationName: locations[0].name,
      reason: 'Wareneingang Bestellung', reference: 'WE-2026-001', actor: 'Frederick',
      occurredAt: createdAt,
    },
    {
      id: `${tenantId}-movement-2`, tenantId, type: 'issue', itemId: articles[1].id,
      itemNumber: articles[1].number, itemName: articles[1].name, quantity: 1,
      sourceLocationId: locations[0].id, sourceLocationCode: locations[0].code, sourceLocationName: locations[0].name,
      targetLocationId: null, targetLocationCode: '', targetLocationName: '',
      reason: 'Werkstattauftrag', reference: '202600132', actor: 'Markus Beispiel',
      occurredAt: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
    },
  ];
  return { locations, articles, balances, movements, countSessions: [] };
}

function balanceKey(itemId: string, locationId: string): string {
  return `${itemId}\u0000${locationId}`;
}

function countLineRecord(state: TenantWarehouseState, line: CountLineState): WarehouseCountLineRecord {
  const article = state.articles.find((item) => item.id === line.itemId);
  const location = state.locations.find((item) => item.id === line.locationId);
  return {
    id: line.id, itemId: line.itemId, itemNumber: article?.number ?? '', itemName: article?.name ?? '', unit: article?.unit ?? 'Stück',
    locationId: line.locationId, locationCode: location?.code ?? '',
    bookQuantity: line.bookQuantity, countedQuantity: line.countedQuantity,
    difference: warehouseCountDifference(line.bookQuantity, line.countedQuantity),
  };
}

function countSessionRecord(state: TenantWarehouseState, tenantId: string, session: CountSessionState, withLines: boolean): WarehouseCountSessionRecord {
  const location = session.locationId ? state.locations.find((item) => item.id === session.locationId) : null;
  const counted = session.lines.filter((line) => line.countedQuantity != null);
  return {
    id: session.id, tenantId, reference: session.reference, status: session.status,
    locationId: session.locationId, locationCode: location?.code ?? '', note: session.note,
    totalLines: session.lines.length,
    countedLines: counted.length,
    differenceLines: counted.filter((line) => warehouseCountDifference(line.bookQuantity, line.countedQuantity) !== 0).length,
    createdAt: session.createdAt, createdByName: 'Frederick', postedAt: session.postedAt,
    lines: withLines ? session.lines.map((line) => countLineRecord(state, line)) : [],
  };
}

function stockRecord(state: TenantWarehouseState, balance: BalanceState): WarehouseStockRecord {
  const article = state.articles.find((item) => item.id === balance.itemId);
  const location = state.locations.find((item) => item.id === balance.locationId);
  if (!article || !location) throw new Error('Der Demobestand enthält eine ungültige Referenz.');
  const available = Math.round((balance.onHand - balance.reserved) * 1000) / 1000;
  return {
    itemId: article.id,
    itemNumber: article.number,
    itemName: article.name,
    unit: article.unit,
    locationId: location.id,
    locationCode: location.code,
    locationName: location.name,
    onHand: balance.onHand,
    reserved: balance.reserved,
    available,
    minimumStock: article.minimumStock,
    lowStock: available < article.minimumStock,
    negativeStock: available < 0,
    preferredSupplierId: article.preferredSupplierId,
    preferredSupplierName: article.preferredSupplierName,
    updatedAt: balance.updatedAt,
  };
}

export class DemoWarehouseGateway implements WarehouseGateway {
  private readonly states = new Map<string, TenantWarehouseState>();

  private state(tenantId: string): TenantWarehouseState {
    const existing = this.states.get(tenantId);
    if (existing) return existing;
    const created = createState(tenantId);
    this.states.set(tenantId, created);
    return created;
  }

  private recalculateLocationCounts(state: TenantWarehouseState): void {
    for (let index = 0; index < state.locations.length; index += 1) {
      const location = state.locations[index];
      const stockedItemCount = state.balances.filter((balance) => (
        balance.locationId === location.id && balance.onHand !== 0
      )).length;
      state.locations[index] = { ...location, stockedItemCount };
    }
  }

  /**
   * Lagerplatz löschen -- angestoßen vom geteilten Löschdialog über die Rückmeldung des
   * Deletion-Gateways. Der Demo-Modus kennt (wie überall) keine echten Verweise, es wird also
   * immer gelöscht; damit hinterher keine Bestände oder Bewegungen auf einen verschwundenen Platz
   * zeigen (und der Bestandsmapper stolpert), gehen sie mit. In Supabase entscheidet stattdessen
   * die Fremdschlüssel-Prüfung: ein bebuchter Platz wird archiviert statt gelöscht.
   */
  removeLocation(tenantId: string, locationId: string): void {
    const state = this.states.get(tenantId);
    if (!state) return;
    const keepLocations = state.locations.filter((location) => location.id !== locationId);
    state.locations.splice(0, state.locations.length, ...keepLocations);
    const keepBalances = state.balances.filter((balance) => balance.locationId !== locationId);
    state.balances.splice(0, state.balances.length, ...keepBalances);
    const keepMovements = state.movements.filter((movement) => (
      movement.sourceLocationId !== locationId && movement.targetLocationId !== locationId
    ));
    state.movements.splice(0, state.movements.length, ...keepMovements);
  }

  async getOverview(tenantId: string): Promise<WarehouseOverview> {
    const state = this.state(tenantId);
    const totals = new Map<string, { onHand: number; reserved: number }>();
    for (const article of state.articles) totals.set(article.id, { onHand: 0, reserved: 0 });
    for (const balance of state.balances) {
      const total = totals.get(balance.itemId) ?? { onHand: 0, reserved: 0 };
      total.onHand += balance.onHand;
      total.reserved += balance.reserved;
      totals.set(balance.itemId, total);
    }
    let lowStockItems = 0;
    let negativeStockItems = 0;
    let stockedItems = 0;
    for (const article of state.articles) {
      const total = totals.get(article.id) ?? { onHand: 0, reserved: 0 };
      const available = total.onHand - total.reserved;
      if (total.onHand !== 0) stockedItems += 1;
      if (available < article.minimumStock) lowStockItems += 1;
      if (available < 0) negativeStockItems += 1;
    }
    const today = new Date().toISOString().slice(0, 10);
    return {
      stockedItems,
      lowStockItems,
      negativeStockItems,
      activeLocations: state.locations.filter((location) => location.active).length,
      movementsToday: state.movements.filter((movement) => movement.occurredAt.startsWith(today)).length,
      openReplenishmentItems: lowStockItems,
    };
  }

  async listLocations(tenantId: string, includeInactive = false): Promise<readonly WarehouseLocationRecord[]> {
    const state = this.state(tenantId);
    this.recalculateLocationCounts(state);
    return clone(state.locations.filter((location) => includeInactive || location.active)
      .sort((left, right) => Number(right.defaultLocation) - Number(left.defaultLocation) || left.code.localeCompare(right.code, 'de')));
  }

  async saveLocation(input: SaveWarehouseLocationInput): Promise<WarehouseLocationRecord> {
    const normalized = normalizeWarehouseLocationInput(input);
    const state = this.state(normalized.tenantId);
    const duplicate = state.locations.find((location) => (
      location.code === normalized.code && location.id !== normalized.locationId
    ));
    if (duplicate) throw new Error('Dieser Lagerplatz-Code wird im Mandanten bereits verwendet.');

    const current = normalized.locationId
      ? state.locations.find((location) => location.id === normalized.locationId)
      : null;
    if (normalized.locationId && !current) throw new Error('Der Lagerplatz wurde in diesem Mandanten nicht gefunden.');
    if (current && !normalized.active && state.balances.some((balance) => balance.locationId === current.id && balance.onHand !== 0)) {
      throw new Error('Ein Lagerplatz mit Bestand kann nicht deaktiviert werden.');
    }

    if (normalized.defaultLocation) {
      for (let index = 0; index < state.locations.length; index += 1) {
        state.locations[index] = { ...state.locations[index], defaultLocation: false };
      }
    }
    const timestamp = now();
    const saved: WarehouseLocationRecord = {
      id: current?.id ?? `${normalized.tenantId}-location-${crypto.randomUUID()}`,
      tenantId: normalized.tenantId,
      code: normalized.code,
      name: normalized.name,
      description: normalized.description ?? '',
      active: normalized.active ?? true,
      defaultLocation: normalized.defaultLocation ?? false,
      stockedItemCount: current?.stockedItemCount ?? 0,
      createdAt: current?.createdAt ?? timestamp,
      updatedAt: timestamp,
    };
    if (current) {
      state.locations[state.locations.findIndex((location) => location.id === current.id)] = saved;
    } else {
      state.locations.push(saved);
    }
    if (!state.locations.some((location) => location.defaultLocation && location.active)) {
      const fallbackIndex = state.locations.findIndex((location) => location.active);
      if (fallbackIndex >= 0) state.locations[fallbackIndex] = { ...state.locations[fallbackIndex], defaultLocation: true };
    }
    return clone(saved);
  }

  async searchArticleOptions(tenantId: string, query = ''): Promise<readonly WarehouseArticleOption[]> {
    const needle = query.trim().toLocaleLowerCase('de');
    return clone(this.state(tenantId).articles.filter((article) => !needle || [
      article.number, article.name, article.preferredSupplierName, article.preferredSupplierItemNumber,
    ].some((value) => value.toLocaleLowerCase('de').includes(needle))).slice(0, 50));
  }

  async searchStock(input: WarehouseStockSearchInput): Promise<WarehouseStockPage> {
    const normalized = normalizeWarehouseStockSearchInput(input);
    const state = this.state(normalized.tenantId);
    const needle = normalized.query.toLocaleLowerCase('de');
    const filtered = state.balances
      .map((balance) => stockRecord(state, balance))
      .filter((stock) => normalized.locationId === 'all' || stock.locationId === normalized.locationId)
      .filter((stock) => normalized.filter === 'all'
        || (normalized.filter === 'low' && stock.lowStock)
        || (normalized.filter === 'negative' && stock.negativeStock))
      .filter((stock) => !needle || [
        stock.itemNumber, stock.itemName, stock.locationCode, stock.locationName, stock.preferredSupplierName,
      ].some((value) => value.toLocaleLowerCase('de').includes(needle)))
      .sort((left, right) => left.itemNumber.localeCompare(right.itemNumber, 'de') || left.locationCode.localeCompare(right.locationCode, 'de'));
    const start = (normalized.page - 1) * normalized.pageSize;
    return {
      items: clone(filtered.slice(start, start + normalized.pageSize)),
      total: filtered.length,
      page: normalized.page,
      pageSize: normalized.pageSize,
    };
  }

  async listMovements(tenantId: string, limit = 100): Promise<readonly WarehouseMovementRecord[]> {
    return clone([...this.state(tenantId).movements]
      .sort((left, right) => right.occurredAt.localeCompare(left.occurredAt))
      .slice(0, Math.min(250, Math.max(1, Math.trunc(limit)))));
  }

  async postMovement(input: PostWarehouseMovementInput): Promise<WarehouseMovementRecord> {
    const normalized = normalizeWarehouseMovementInput(input);
    const state = this.state(normalized.tenantId);
    const article = state.articles.find((item) => item.id === normalized.itemId);
    if (!article) throw new Error('Der Artikel wurde in diesem Mandanten nicht gefunden.');
    const source = normalized.sourceLocationId
      ? state.locations.find((location) => location.id === normalized.sourceLocationId && location.active)
      : null;
    const target = normalized.targetLocationId
      ? state.locations.find((location) => location.id === normalized.targetLocationId && location.active)
      : null;
    if (normalized.sourceLocationId && !source) throw new Error('Der Quelllagerplatz ist nicht verfügbar.');
    if (normalized.targetLocationId && !target) throw new Error('Der Ziellagerplatz ist nicht verfügbar.');

    const getBalance = (locationId: string): BalanceState => {
      let balance = state.balances.find((item) => balanceKey(item.itemId, item.locationId) === balanceKey(article.id, locationId));
      if (!balance) {
        balance = { itemId: article.id, locationId, onHand: 0, reserved: 0, updatedAt: now() };
        state.balances.push(balance);
      }
      return balance;
    };
    const sourceBalance = source ? getBalance(source.id) : null;
    const targetBalance = target ? getBalance(target.id) : null;
    const sourceDelta = normalized.type === 'issue' || normalized.type === 'transfer' ? -normalized.quantity : 0;
    const targetDelta = normalized.type === 'receipt' || normalized.type === 'transfer'
      ? normalized.quantity
      : normalized.type === 'adjustment' ? normalized.quantity : 0;
    if (sourceBalance && sourceBalance.onHand + sourceDelta < 0) {
      throw new Error('Die Buchung würde einen negativen Bestand erzeugen.');
    }
    if (targetBalance && targetBalance.onHand + targetDelta < 0) {
      throw new Error('Die Korrektur würde einen negativen Bestand erzeugen.');
    }

    const timestamp = now();
    if (sourceBalance) {
      sourceBalance.onHand = Math.round((sourceBalance.onHand + sourceDelta) * 1000) / 1000;
      sourceBalance.updatedAt = timestamp;
    }
    if (targetBalance) {
      targetBalance.onHand = Math.round((targetBalance.onHand + targetDelta) * 1000) / 1000;
      targetBalance.updatedAt = timestamp;
    }
    const movement: WarehouseMovementRecord = {
      id: `${normalized.tenantId}-movement-${crypto.randomUUID()}`,
      tenantId: normalized.tenantId,
      type: normalized.type,
      itemId: article.id,
      itemNumber: article.number,
      itemName: article.name,
      quantity: normalized.quantity,
      sourceLocationId: source?.id ?? null,
      sourceLocationCode: source?.code ?? '',
      sourceLocationName: source?.name ?? '',
      targetLocationId: target?.id ?? null,
      targetLocationCode: target?.code ?? '',
      targetLocationName: target?.name ?? '',
      reason: normalized.reason,
      reference: normalized.reference ?? '',
      actor: 'Frederick',
      occurredAt: timestamp,
    };
    state.movements.unshift(movement);
    this.recalculateLocationCounts(state);
    return clone(movement);
  }

  async listReplenishment(tenantId: string): Promise<readonly WarehouseReplenishmentRecord[]> {
    const state = this.state(tenantId);
    return clone(state.articles.map((article): WarehouseReplenishmentRecord | null => {
      const balances = state.balances.filter((balance) => balance.itemId === article.id);
      const onHand = balances.reduce((sum, balance) => sum + balance.onHand, 0);
      const reserved = balances.reduce((sum, balance) => sum + balance.reserved, 0);
      const available = Math.round((onHand - reserved) * 1000) / 1000;
      const suggestedQuantity = Math.max(0, Math.round((article.minimumStock - available) * 1000) / 1000);
      if (suggestedQuantity <= 0) return null;
      return {
        itemId: article.id,
        itemNumber: article.number,
        itemName: article.name,
        unit: article.unit,
        onHand,
        reserved,
        available,
        minimumStock: article.minimumStock,
        suggestedQuantity,
        urgency: available <= 0 ? 'critical' : 'low',
        supplierId: article.preferredSupplierId,
        supplierName: article.preferredSupplierName,
        supplierItemNumber: article.preferredSupplierItemNumber,
        purchasePrice: article.preferredPurchasePrice,
        leadTimeDays: article.preferredLeadTimeDays,
      };
    }).filter((item): item is WarehouseReplenishmentRecord => item !== null)
      .sort((left, right) => (left.urgency === right.urgency ? right.suggestedQuantity - left.suggestedQuantity : left.urgency === 'critical' ? -1 : 1)));
  }

  async openCountSession(input: OpenWarehouseCountInput): Promise<WarehouseCountSessionRecord> {
    const state = this.state(input.tenantId);
    const locationId = input.locationId ?? null;
    if (locationId && !state.locations.some((location) => location.id === locationId)) {
      throw new Error('Der Lagerplatz wurde nicht gefunden.');
    }
    const trackedItemIds = new Set(state.articles.map((article) => article.id));
    const lines: CountLineState[] = state.balances
      .filter((balance) => trackedItemIds.has(balance.itemId) && (!locationId || balance.locationId === locationId))
      .map((balance) => ({
        id: `${input.tenantId}-count-line-${crypto.randomUUID()}`,
        itemId: balance.itemId, locationId: balance.locationId,
        bookQuantity: balance.onHand, countedQuantity: null,
      }));
    const session: CountSessionState = {
      id: `${input.tenantId}-count-${crypto.randomUUID()}`,
      reference: `INV-26-${String(state.countSessions.length + 1).padStart(4, '0')}`,
      status: 'counting', locationId, note: input.note?.trim() ?? '',
      createdAt: now(), postedAt: null, lines,
    };
    state.countSessions.unshift(session);
    return countSessionRecord(state, input.tenantId, session, true);
  }

  async listCountSessions(tenantId: string): Promise<readonly WarehouseCountSessionRecord[]> {
    const state = this.state(tenantId);
    return state.countSessions.map((session) => countSessionRecord(state, tenantId, session, false));
  }

  async getCountSession(tenantId: string, sessionId: string): Promise<WarehouseCountSessionRecord> {
    const state = this.state(tenantId);
    const session = state.countSessions.find((item) => item.id === sessionId);
    if (!session) throw new Error('Die Inventur wurde nicht gefunden.');
    return countSessionRecord(state, tenantId, session, true);
  }

  async saveCountLine(input: SaveWarehouseCountLineInput): Promise<WarehouseCountLineRecord> {
    const state = this.state(input.tenantId);
    const session = state.countSessions.find((item) => item.id === input.sessionId);
    if (!session) throw new Error('Die Inventur wurde nicht gefunden.');
    if (session.status !== 'counting') throw new Error('Diese Inventur ist bereits abgeschlossen.');
    const line = session.lines.find((item) => item.id === input.lineId);
    if (!line) throw new Error('Die Zählposition wurde nicht gefunden.');
    if (input.countedQuantity != null && input.countedQuantity < 0) throw new Error('Die gezählte Menge darf nicht negativ sein.');
    line.countedQuantity = input.countedQuantity == null ? null : Math.round(Number(input.countedQuantity) * 1000) / 1000;
    return countLineRecord(state, line);
  }

  async postCountSession(input: PostWarehouseCountInput): Promise<WarehouseCountSessionRecord> {
    const state = this.state(input.tenantId);
    const session = state.countSessions.find((item) => item.id === input.sessionId);
    if (!session) throw new Error('Die Inventur wurde nicht gefunden.');
    if (session.status !== 'counting') throw new Error('Diese Inventur ist bereits abgeschlossen.');
    const timestamp = now();
    for (const line of session.lines) {
      if (line.countedQuantity == null) continue; // ungezählte Positionen bleiben unberührt
      let balance = state.balances.find((item) => balanceKey(item.itemId, item.locationId) === balanceKey(line.itemId, line.locationId));
      if (!balance) {
        balance = { itemId: line.itemId, locationId: line.locationId, onHand: 0, reserved: 0, updatedAt: timestamp };
        state.balances.push(balance);
      }
      // Differenz gegen den AKTUELLEN Bestand buchen (nicht gegen den Snapshot) -- so gehen zwischenzeitliche
      // Bewegungen nicht verloren. Die Korrektur läuft über denselben Weg wie eine normale Korrektur-Buchung.
      const delta = Math.round((line.countedQuantity - balance.onHand) * 1000) / 1000;
      if (delta === 0) continue;
      const article = state.articles.find((item) => item.id === line.itemId);
      const location = state.locations.find((item) => item.id === line.locationId);
      balance.onHand = Math.round((balance.onHand + delta) * 1000) / 1000;
      balance.updatedAt = timestamp;
      state.movements.unshift({
        id: `${input.tenantId}-movement-${crypto.randomUUID()}`, tenantId: input.tenantId, type: 'adjustment',
        itemId: line.itemId, itemNumber: article?.number ?? '', itemName: article?.name ?? '', quantity: delta,
        sourceLocationId: null, sourceLocationCode: '', sourceLocationName: '',
        targetLocationId: line.locationId, targetLocationCode: location?.code ?? '', targetLocationName: location?.name ?? '',
        reason: `Inventur ${session.reference}`, reference: session.reference, actor: 'Frederick', occurredAt: timestamp,
      });
    }
    session.status = 'posted';
    session.postedAt = timestamp;
    this.recalculateLocationCounts(state);
    return countSessionRecord(state, input.tenantId, session, true);
  }
}

export function createDemoWarehouseGateway(): DemoWarehouseGateway {
  return new DemoWarehouseGateway();
}
